import cx from "classnames";
import type { ReactElement } from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "tertiary" | "ghost";
  size?: "small" | "medium" | "large";
  disabled?: boolean;
  icon?: ReactElement;
  iconPosition?: "left" | "right";
}

export default function Button({
  variant = "primary",
  size = "medium",
  disabled,
  icon,
  iconPosition,
  className,
  children,
  ...props
}: ButtonProps): ReactElement {
  // Check if `children` is truly empty (undefined, null, or an empty string)
  const isIconOnly = !children || children === "";

  return (
    <button
      disabled={disabled}
      className={cx(
        "rounded-full font-medium transition-all duration-200 flex items-center flex-nowrap w-auto",
        {
          // Variant Styles
          "bg-button-primary text-button-primary-label hover:bg-button-primary-hover":
            variant === "primary",
          "bg-button-secondary text-button-primary-label hover:bg-button-secondary-hover":
            variant === "secondary",
          "bg-button-tertiary text-button-secondary-label hover:bg-button-tertiary-hover":
            variant === "tertiary",
          "bg-transparent text-button-secondary-label hover:bg-button-ghost":
            variant === "ghost",

          // Size Styles
          "px-2 py-1 text-button-2 gap-1 font-semibold":
            size === "small" && !isIconOnly,
          "px-3 py-2 text-button-1 gap-2 font-medium":
            size === "medium" && !isIconOnly,
          "px-4 py-2 text-button-1 gap-2 font-medium":
            size === "large" && !isIconOnly,

          // Icon-Only Button Padding
          "p-[6px]":
            (isIconOnly && size === "small") ||
            (isIconOnly && size === "medium"),
          "p-2": isIconOnly && size === "large",

          // Disabled Styles
          "bg-opacity-40 opacity-40 pointer-events-none": disabled,

          // Icon Position Styles
          "flex-row": icon && iconPosition === "left",
          "flex-row-reverse": icon && iconPosition === "right",
        },
        className,
      )}
      {...props}
    >
      {icon && (
        <span
          className={cx("flex items-center justify-center", {
            "w-3 h-3": size === "small",
            "w-5 h-5": size === "medium",
            "w-6 h-6": size === "large",
          })}
        >
          {icon}
        </span>
      )}
      {children}
    </button>
  );
}
